import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { MemberLifecycle } from '../Models/MemberLifecycle/MemberLifecycle.model';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';
import { setShowMessage } from './shared.slice';

let memberLifecyclePollingTimer: ReturnType<typeof setTimeout> | null = null;

const MEMBER_LIFECYCLE_POLL_INTERVAL_MS = 5000;
const MEMBER_LIFECYCLE_POLL_MAX_DURATION_MS = 10 * 60 * 1000;
const MEMBER_LIFECYCLE_POLL_MAX_CONSECUTIVE_ERRORS = 3;

enum MemberLifecycleCalculationStatus {
    Queued = 0,
    InProgress = 1,
    Completed = 2,
    Failed = 3,
}

const clearMemberLifecyclePollingTimer = () => {
    if (memberLifecyclePollingTimer) {
        clearTimeout(memberLifecyclePollingTimer);
        memberLifecyclePollingTimer = null;
    }
};

const memberLifecycle = createSlice({
    name: 'memberLifecycle',
    initialState: {
        memberLifecycle: null as MemberLifecycle | null,
        isLoading: false,
        isCalculating: false,
        calculationJobId: '',
        error: '',
    },
    reducers: {
        _setMemberLifecycle(state, action) {
            state.memberLifecycle = action.payload;
        },
        _setIsLoading(state, action) {
            state.isLoading = action.payload;
        },
        _setIsCalculating(state, action) {
            state.isCalculating = action.payload;
        },
        _setCalculationJobId(state, action) {
            state.calculationJobId = action.payload;
        },
        _setError(state, action) {
            state.error = action.payload;
        },
    },
});

const { _setMemberLifecycle, _setIsLoading, _setIsCalculating, _setCalculationJobId, _setError } = memberLifecycle.actions;

export const clearMemberLifecyclePolling = (): AppThunk => (dispatch) => {
    clearMemberLifecyclePollingTimer();
    dispatch(_setIsCalculating(false));
    dispatch(_setCalculationJobId(''));
};

const pollMemberLifecycleCalculation =
    (jobId: string, memberId: string | undefined, skipMemberFetch: boolean): AppThunk<Promise<void>> =>
    (dispatch) =>
        new Promise<void>((resolve) => {
            const startedAt = Date.now();
            let consecutiveErrors = 0;

            const pollStatus = async () => {
                if (Date.now() - startedAt > MEMBER_LIFECYCLE_POLL_MAX_DURATION_MS) {
                    clearMemberLifecyclePollingTimer();
                    dispatch(_setIsCalculating(false));
                    dispatch(_setCalculationJobId(''));
                    dispatch(setShowMessage(true, 'Member lifecycle calculation timed out.', 'error'));
                    resolve();
                    return;
                }

                try {
                    const statusResponse = await axios.get(`${API_URL}/MemberLifecycle/calculate/status/${jobId}`);
                    consecutiveErrors = 0;
                    const status = statusResponse.data || {};
                    const statusValue = status?.Status ?? status?.status;

                    const isCompleted =
                        statusValue === MemberLifecycleCalculationStatus.Completed || statusValue === 'Completed' || statusValue === 2;
                    const isFailed =
                        statusValue === MemberLifecycleCalculationStatus.Failed || statusValue === 'Failed' || statusValue === 3;

                    if (isCompleted) {
                        clearMemberLifecyclePollingTimer();
                        const membersProcessed = status?.MembersProcessed ?? status?.membersProcessed;
                        dispatch(_setIsCalculating(false));
                        dispatch(_setCalculationJobId(''));
                        dispatch(
                            setShowMessage(
                                true,
                                membersProcessed != null
                                    ? `Member lifecycle calculated for ${membersProcessed} member(s)`
                                    : 'Member lifecycle calculated successfully',
                                'success',
                            ),
                        );

                        if (memberId && !skipMemberFetch) {
                            await dispatch(fetchMemberLifecycle(memberId));
                        }

                        resolve();
                        return;
                    }

                    if (isFailed) {
                        clearMemberLifecyclePollingTimer();
                        dispatch(_setIsCalculating(false));
                        dispatch(_setCalculationJobId(''));
                        const errorMessage = status?.ErrorMessage ?? status?.errorMessage ?? 'Member lifecycle calculation failed.';
                        dispatch(setShowMessage(true, errorMessage, 'error'));
                        resolve();
                        return;
                    }

                    memberLifecyclePollingTimer = setTimeout(pollStatus, MEMBER_LIFECYCLE_POLL_INTERVAL_MS);
                } catch {
                    consecutiveErrors += 1;
                    if (consecutiveErrors >= MEMBER_LIFECYCLE_POLL_MAX_CONSECUTIVE_ERRORS) {
                        clearMemberLifecyclePollingTimer();
                        dispatch(_setIsCalculating(false));
                        dispatch(_setCalculationJobId(''));
                        dispatch(setShowMessage(true, 'Unable to retrieve member lifecycle calculation status.', 'error'));
                        resolve();
                        return;
                    }

                    memberLifecyclePollingTimer = setTimeout(pollStatus, MEMBER_LIFECYCLE_POLL_INTERVAL_MS);
                }
            };

            void pollStatus();
        });

export const fetchMemberLifecycle =
    (memberId: string): AppThunk =>
    async (dispatch) => {
        if (!memberId) {
            dispatch(_setMemberLifecycle(null));
            return;
        }

        dispatch(_setIsLoading(true));
        dispatch(_setError(''));
        try {
            const response = await axios.get(`${API_URL}/MemberLifecycle/${memberId}`);
            dispatch(_setMemberLifecycle(response.data));
        } catch {
            dispatch(_setError('Unable to load member lifecycle.'));
            dispatch(_setMemberLifecycle(null));
        } finally {
            dispatch(_setIsLoading(false));
        }
    };

export const recalculateMemberLifecycle =
    (memberId?: string, skipMemberFetch = false): AppThunk<Promise<void>> =>
    async (dispatch) => {
        dispatch(_setIsLoading(true));
        dispatch(_setError(''));
        clearMemberLifecyclePollingTimer();

        try {
            const url = memberId
                ? `${API_URL}/MemberLifecycle/calculate?memberId=${encodeURIComponent(memberId)}`
                : `${API_URL}/MemberLifecycle/calculate`;
            const response = await axios.post(url);
            const jobId = response.data?.JobId ?? response.data?.jobId;

            if (jobId) {
                dispatch(_setIsLoading(false));
                dispatch(_setIsCalculating(true));
                dispatch(_setCalculationJobId(jobId));
                dispatch(setShowMessage(true, 'Member lifecycle calculation started. Progress will update automatically.', 'info'));
                await dispatch(pollMemberLifecycleCalculation(jobId, memberId, skipMemberFetch));
                return;
            }

            const membersProcessed = response.data?.MembersProcessed ?? response.data?.membersProcessed;

            dispatch(
                setShowMessage(
                    true,
                    membersProcessed != null
                        ? `Member lifecycle calculated for ${membersProcessed} member(s)`
                        : 'Member lifecycle calculated successfully',
                    'success',
                ),
            );

            if (memberId && !skipMemberFetch) {
                await dispatch(fetchMemberLifecycle(memberId));
            } else {
                dispatch(_setIsLoading(false));
            }
        } catch {
            dispatch(setShowMessage(true, 'Unable to calculate member lifecycle.', 'error'));
            dispatch(_setIsLoading(false));
            dispatch(_setIsCalculating(false));
            dispatch(_setCalculationJobId(''));
        }
    };

export default memberLifecycle.reducer;
